import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import api from '../../../api/axios';

const AddUserModal = ({ isOpen, onClose, onUserAdded }) => {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    accessLevel: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const accessLevelOptions = [
    { value: 'ADMIN', label: 'Admin' },
    { value: 'TEAM_MEMBER', label: 'Team Member' }
  ];

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAccessLevelChange = (value) => {
    setFormData(prev => ({ ...prev, accessLevel: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await api.post('/api/v1/settings/users/add', {
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        password: formData.password,
        role: formData.accessLevel
      });
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setFormData({ email: '', firstName: '', lastName: '', password: '', accessLevel: '' });
        onUserAdded?.();
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add user');
    } finally {
      setSubmitting(false);
    }
  };

  const isValidInput = formData?.email && formData?.firstName && formData?.lastName && formData?.password && formData?.accessLevel;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <h2 className="text-lg md:text-xl font-semibold text-foreground">Add User</h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-muted transition-colors"
            type="button"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Success Message */}
        {showSuccess && (
          <div className="m-4 md:m-6 p-4 bg-success/10 border border-success rounded-lg flex items-center gap-3">
            <Icon name="CheckCircle" size={20} className="text-success" />
            <p className="text-sm font-medium text-success">User added successfully</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="m-4 md:m-6 p-4 bg-error/10 border border-error rounded-lg flex items-center gap-3">
            <Icon name="AlertCircle" size={20} className="text-error" />
            <p className="text-sm font-medium text-error">{error}</p>
          </div>
        )}

        {/* Form */}
        {!showSuccess && (
          <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Email <span className="text-error">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData?.email}
                onChange={handleChange}
                required
                placeholder="Enter email address"
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                First Name <span className="text-error">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData?.firstName}
                onChange={handleChange}
                required
                placeholder="Enter first name"
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Last Name <span className="text-error">*</span>
              </label>
              <input
                type="text"
                name="lastName"
                value={formData?.lastName}
                onChange={handleChange}
                required
                placeholder="Enter last name"
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Password <span className="text-error">*</span>
              </label>
              <input
                type="password"
                name="password"
                value={formData?.password}
                onChange={handleChange}
                required
                placeholder="Enter password"
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Access Level */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Access Level <span className="text-error">*</span>
              </label>
              <Select
                options={accessLevelOptions}
                value={formData?.accessLevel}
                onChange={handleAccessLevelChange}
                placeholder="Select access level"
                required
              />
            </div>

            {/* Submit Button */}
            <div className="pt-2">
              <Button
                type="submit"
                disabled={!isValidInput || submitting}
                fullWidth
                iconName="Send"
                className="disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Adding...' : 'Invite'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddUserModal;
